import React from 'react';
import { Badge } from '../components/ui/badge';
import { Shield, CheckCircle } from 'lucide-react';

const NDPR = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0066FF 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0066FF 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-10 right-10 w-32 md:w-64 h-32 md:h-64 bg-[#0066FF]/20 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Badge className="bg-white/10 text-white hover:bg-white/20 border-0 mb-4">
            <Shield className="w-4 h-4 mr-2" />
            Legal
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-white">
            NDPR Compliance
          </h1>
          <p className="text-gray-300 text-center">Nigeria Data Protection Regulation</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our NDPR Commitment</h2>
          <p className="text-gray-600 mb-6 text-center md:text-left">
            HRMQore is committed to ensuring compliance with the Nigeria Data Protection Regulation (NDPR) issued by the National Information Technology Development Agency (NITDA). We have implemented comprehensive measures to protect the personal data of our users and their employees in accordance with Nigerian data protection laws.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">What is NDPR?</h2>
          <p className="text-gray-600 mb-6 text-center md:text-left">
            The Nigeria Data Protection Regulation (NDPR) was issued in January 2019 to safeguard the rights of natural persons to data privacy. It applies to all transactions intended for the processing of personal data, and to the actual processing of personal data, regardless of the means by which the data processing is conducted.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights Under NDPR</h2>
          <div className="space-y-4 mb-6">
            {[
              { title: "Right to Information", desc: "You have the right to know how your personal data is being collected, used, and processed." },
              { title: "Right to Access", desc: "You can request access to your personal data that we hold and obtain a copy of it." },
              { title: "Right to Rectification", desc: "You can request correction of inaccurate or incomplete personal data." },
              { title: "Right to Erasure", desc: "You can request deletion of your personal data under certain circumstances." },
              { title: "Right to Object", desc: "You can object to the processing of your personal data for direct marketing purposes." },
              { title: "Right to Data Portability", desc: "You can receive your personal data in a structured, commonly used format." },
              { title: "Right to Withdraw Consent", desc: "Where processing is based on consent, you can withdraw that consent at any time." }
            ].map((right, idx) => (
              <div key={idx} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">{right.title}</h3>
                  <p className="text-gray-600 text-sm">{right.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Processing Principles</h2>
          <p className="text-gray-600 mb-4 text-center md:text-left">In compliance with NDPR, we adhere to the following principles:</p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li><strong>Lawfulness:</strong> Personal data is collected and processed lawfully with appropriate legal basis</li>
            <li><strong>Consent:</strong> Where required, we obtain clear and informed consent before processing personal data</li>
            <li><strong>Purpose Limitation:</strong> Data is collected for specified, explicit, and legitimate purposes only</li>
            <li><strong>Data Minimization:</strong> We only collect data that is necessary for the stated purposes</li>
            <li><strong>Accuracy:</strong> We take reasonable steps to ensure personal data is accurate and up to date</li>
            <li><strong>Storage Limitation:</strong> Personal data is kept only as long as necessary for the purposes collected</li>
            <li><strong>Security:</strong> We implement appropriate technical and organizational measures to protect personal data</li>
            <li><strong>Accountability:</strong> We are accountable for demonstrating compliance with NDPR</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Protection Measures</h2>
          <p className="text-gray-600 mb-4 text-center md:text-left">HRMQore implements the following security measures:</p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li>Encryption of personal data at rest and in transit using industry-standard protocols</li>
            <li>Access controls and authentication mechanisms to prevent unauthorized access</li>
            <li>Regular security assessments and penetration testing</li>
            <li>Employee training on data protection and privacy</li>
            <li>Incident response procedures for data breaches</li>
            <li>Regular data backups and disaster recovery procedures</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Data Sharing</h2>
          <p className="text-gray-600 mb-6 text-center md:text-left">
            We only share personal data with third parties when necessary to provide our services, and we ensure that all third parties are contractually bound to protect your data in accordance with NDPR requirements. We do not sell personal data to third parties.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Breach Notification</h2>
          <p className="text-gray-600 mb-6 text-center md:text-left">
            In the event of a data breach that is likely to result in a risk to the rights and freedoms of data subjects, we will notify NITDA within 72 hours of becoming aware of the breach. We will also notify affected individuals without undue delay where the breach is likely to result in high risk to their rights and freedoms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
          <p className="text-gray-600 mb-6 text-center md:text-left">
            When transferring personal data outside Nigeria, we ensure that adequate safeguards are in place to protect the data. This includes implementing appropriate contractual clauses and ensuring the receiving country provides adequate data protection.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Exercise Your Rights</h2>
          <p className="text-gray-600 mb-4 text-center md:text-left">
            To exercise any of your NDPR rights, please contact our Data Protection Officer:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg">
            <p className="text-gray-700 font-semibold">Data Protection Officer</p>
            <p className="text-gray-600">HRMQore</p>
            <p className="text-gray-600">Email: dpo@hrmqore.com</p>
            <p className="text-gray-600">Phone: 07042050336</p>
          </div>

          <div className="mt-8 p-6 bg-[#0066FF]/5 rounded-lg border border-[#0066FF]/20">
            <h3 className="font-semibold text-gray-900 mb-2">Regulatory Authority</h3>
            <p className="text-gray-600 text-sm">
              The NDPR is enforced by the National Information Technology Development Agency (NITDA). If you believe your data protection rights have been violated, you have the right to lodge a complaint with NITDA.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NDPR;
